<template>
  <div class="countdown-settings-panel">
    <div class="setting-group">
      <label class="setting-label" for="mainText">MAIN TEXT</label>
      <input class="setting-input" type="text" id="mainText" v-model="localMainText" @input="updateSetting('mainText', localMainText)" />
    </div>

    <div class="setting-group">
      <label class="setting-label" for="targetDate">DEADLINE</label>
      <input
        class="setting-input"
        type="datetime-local"
        id="targetDate"
        :value="formatDateForInput(localTargetDate)"
        @input="handleTargetDateInput($event.target.value)"
      />
    </div>

    <div class="setting-group">
      <label class="setting-label">BACKGROUND COLOUR/IMAGE</label>
      <div class="color-picker-wrapper">
        <input type="color" v-model="localBackgroundColor" @input="updateSetting('backgroundColor', localBackgroundColor)" />
        <div class="file-upload-container">
          <label class="select-file-button">
            <input type="file" 
              accept="image/*" 
              @change="handleFileUpload"
              style="display: none"
              ref="fileInput"
            />
            Select File
          </label>
          <span v-if="uploadedFileName" class="file-name">{{ uploadedFileName }}</span>
        </div>
      </div>
    </div>

    <div class="setting-group switch-setting">
      <label class="setting-label" for="transparentBackground">TRANSPARENT BACKGROUND</label>
      <label class="switch">
        <input type="checkbox" id="transparentBackground" v-model="localTransparentBackground" @change="updateSetting('transparentBackground', localTransparentBackground)" />
        <span class="slider round"></span>
      </label>
    </div>

    <div class="setting-group">
      <label class="setting-label" for="fontFamily">FONT TYPE/COLOUR</label>
      <div class="font-selection">
        <select id="fontFamily" v-model="localFontFamily" @change="updateSetting('fontFamily', localFontFamily)" class="setting-input">
          <option value="Arial">Default</option>
          <option value="Verdana">Verdana</option>
          <option value="Georgia">Georgia</option>
          <option value="Times New Roman">Times New Roman</option>
        </select>
        <input type="color" v-model="localLabelColor" @input="updateSetting('labelColor', localLabelColor)" class="font-color-picker" />
      </div>
    </div>

    <div class="setting-group">
      <label class="setting-label" for="particleEffect">PARTICLE EFFECT</label>
      <select id="particleEffect" v-model="localParticleEffect" @change="updateSetting('particleEffect', localParticleEffect)" class="setting-input">
        <option value="Off">Off</option>
        <option value="Snow">Snow</option>
        <option value="Stars">Stars</option>
      </select>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CountdownSettings',
  data() {
    return {
      // Initialize local data properties with default values or empty strings
      // These will be updated by the watcher when activeObjectProps changes
      localMainText: '',
      localTargetDate: '',
      localDisplayFormat: 'DD:HH:MM:SS',
      localLabelColor: '#FFFFFF',
      localValueColor: '#FFFFFF',
      localBackgroundColor: '#2c4360',
      localTransparentBackground: false,
      localFontSize: 100,
      localFontFamily: 'Arial',
      localShowLabels: true,
      localParticleEffect: 'Off',
      backgroundImage: null,
      backgroundImageOpacity: 1,
      uploadedFileName: '',
    };
  },
  computed: {
    // This computed property directly accesses the activeObjectprops from your Vuex store
    activeObjectProps() {
      return this.$store.state.activeObjectprops;
    },
    // Assuming this getter provides canvas dimensions
    getActiveCanvas() {
        return this.$store.getters.getCurrentActiveCanvas
    }
  },
  watch: {
    // This watcher is crucial. It listens for changes in activeObjectProps.
    activeObjectProps: {
      handler(updatedValues) {
        // console.log("CountdownSettings Watcher: activeObjectProps updated to:", updatedValues); // Debug log
        // Only update local data if the active object is indeed a 'countdownApp'
        if (updatedValues && updatedValues.type === 'countdownApp') {
          // console.log("CountdownSettings Watcher: It's a countdownApp, updating local data."); // Debug log
          this.localMainText = updatedValues.mainText;
          
          // Handle target date update
          if (updatedValues.targetDate !== this.localTargetDate) {
            this.localTargetDate = updatedValues.targetDate;
            if (updatedValues.targetDate) {
              const date = new Date(updatedValues.targetDate);
              this.updateSetting('deadline', this.formatDateForDisplay(date));
            }
          }
          
          this.localDisplayFormat = updatedValues.displayFormat;
          this.localLabelColor = updatedValues.labelColor;
          this.localValueColor = updatedValues.valueColor;
          this.localBackgroundColor = updatedValues.backgroundColor;
          this.localTransparentBackground = updatedValues.transparentBackground;
          this.localFontSize = updatedValues.fontSize;
          this.localFontFamily = updatedValues.fontFamily;
          this.localShowLabels = updatedValues.showLabels;
          this.localParticleEffect = updatedValues.particleEffect;
        } else {
          // console.log("CountdownSettings Watcher: Not a countdownApp or updatedValues is null/undefined."); // Debug log
          // Optionally reset local data if a different app type is selected
          this.localMainText = '';
          this.localTargetDate = '';
          this.localDisplayFormat = 'DD:HH:MM:SS';
          this.localLabelColor = '#FFFFFF';
          this.localValueColor = '#FFFFFF';
          this.localBackgroundColor = '#2c4360';
          this.localTransparentBackground = false;
          this.localFontSize = 100;
          this.localFontFamily = 'Arial';
          this.localShowLabels = true;
          this.localParticleEffect = 'Off';
        }
      },
      deep: true, // Watch for nested changes within the object
      immediate: true, // Run the handler immediately when the component is created
    },
  },
  methods: {
    // This method dispatches an action to your Vuex store to update a specific property
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        try {
          // Update the file name display
          this.uploadedFileName = file.name;

          // Create file reader for image preview
          const reader = new FileReader();
          
          reader.onload = (e) => {
            const imageData = e.target.result;
            this.backgroundImage = imageData;
            
            // Basic object updates
            const updates = {
              type: 'countdownApp',
              backgroundColor: this.localBackgroundColor, // Preserve current background color
              backgroundImage: imageData,
              image: imageData,
              backgroundType: 'image',
              opacity: 1,
              transparent: false,
              isTransparent: false
            };

            // Style object for background
            const styles = {
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url('${imageData}')`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundColor: this.localBackgroundColor,
              opacity: 1,
              zIndex: 1
            };

            // Combine everything
            this.$store.dispatch('setCurrentObjectProperties', {
              ...updates,
              style: styles
            });

            // Log success for debugging
            console.log('Image uploaded successfully:', {
              fileName: file.name,
              imageSize: e.target.result.length,
              opacity: this.localTransparentBackground ? 0 : 1
            });
          };

          reader.onerror = (error) => {
            console.error('Error reading file:', error);
            this.uploadedFileName = '';
            this.backgroundImage = null;
            this.$refs.fileInput.value = '';
          };

          reader.readAsDataURL(file);
        } catch (error) {
          console.error('File upload error:', error);
          this.uploadedFileName = '';
          this.backgroundImage = null;
          this.$refs.fileInput.value = '';
        }
      } else {
        this.uploadedFileName = '';
        this.backgroundImage = null;
        this.$store.dispatch('setCurrentObjectProperties', {
          backgroundImage: null,
          backgroundColor: this.localBackgroundColor
        });
      }
    },

    updateSetting(propName, value) {
      let updates = {};
      let opacity;

      // Debug log
      console.log('Updating setting:', { propName, value });

      switch(propName) {
        case 'transparentBackground':
          opacity = value ? 0 : 1; // Use 0 for full transparency, 1 for solid
          
          if (this.backgroundImage) {
            // Handle case with background image
            updates = {
              type: 'countdownApp',
              transparentBackground: value,
              backgroundType: 'image',
              backgroundImage: this.backgroundImage,
              opacity: opacity,
              isTransparent: value,
              transparent: value,
              style: {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url('${this.backgroundImage}')`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundColor: this.localBackgroundColor,
                opacity: opacity,
                zIndex: 1
              }
            };
          } else if (this.localBackgroundColor) {
            // Handle case with only background color
            /* eslint-disable no-unused-vars */
            const hex = this.localBackgroundColor.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            /* eslint-enable no-unused-vars */
            
            updates = {
              type: 'countdownApp',
              transparentBackground: value,
              isTransparent: value,
              transparent: value,
              opacity: opacity,
              backgroundColor: this.localBackgroundColor,
              style: {
                backgroundColor: this.localBackgroundColor,
                opacity: opacity
              }
            };
          }
          break;

        case 'fontFamily':
          updates = {
            type: 'countdownApp',
            fontFamily: value,
            textStyle: {
              ...this.activeObjectProps?.textStyle,
              fontFamily: value
            }
          };
          break;

        case 'labelColor':
          updates = {
            type: 'countdownApp',
            labelColor: value,
            textStyle: {
              ...this.activeObjectProps?.textStyle,
              color: value
            }
          };
          break;

        case 'particleEffect':
          updates = {
            particleEffect: value,
            particleConfig: {
              enabled: value !== 'Off',
              type: value.toLowerCase(),
              zIndex: 1,
              direction: value === 'Stars' ? 'topRight-bottomLeft' : 'top-bottom'
            }
          };
          break;

        case 'backgroundColor':
          updates = {
            type: 'countdownApp',
            backgroundColor: value,
            style: {
              backgroundColor: value,
              opacity: this.localTransparentBackground ? 0 : 1
            }
          };
          
          if (this.backgroundImage) {
            updates.backgroundType = 'image';
            updates.backgroundImage = this.backgroundImage;
            updates.style = {
              ...updates.style,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url('${this.backgroundImage}')`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              zIndex: 1
            };
          }
          break;

        default:
          updates = { [propName]: value };
      }

      this.$store.dispatch('setCurrentObjectProperties', updates);
    },
    // Special handler for datetime-local input to convert to ISO string for the store
    handleTargetDateInput(value) {
      if (!value) {
        this.localTargetDate = '';
        this.updateSetting('targetDate', '');
        return;
      }
      
      // Create a new date from the input value
      const selectedDate = new Date(value);
      
      // Ensure valid date
      if (!isNaN(selectedDate.getTime())) {
        const isoString = selectedDate.toISOString();
        this.localTargetDate = isoString;
        // Update both targetDate and deadline (for backward compatibility)
        this.updateSetting('targetDate', isoString);
        
        // Also format as YYYY-MM-DD HH:mm:ss for the countdown component
        const formattedDate = this.formatDateForDisplay(selectedDate);
        this.updateSetting('deadline', formattedDate);
      }
    },
    // Formats an ISO string from the store to "YYYY-MM-DDTHH:MM" for datetime-local input
    formatDateForInput(isoString) {
      if (!isoString) return '';
      try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) {
          return ''; // Return empty string for invalid dates
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch (error) {
        console.error("Error formatting date for input:", error, isoString);
        return '';
      }
    },

    formatDateForDisplay(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    // Duration methods removed as they're not needed
  },
};
</script>

<style scoped>
.countdown-settings-panel {
  padding: 14px 12px;
  background: #fff;
  font-family: 'Inter', Arial, sans-serif;
  min-width: 220px;
}

.setting-group {
  margin-bottom: 14px;
  position: relative;
}

/* Particle effects container styles */
.particle-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.setting-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #7a7a7a;
  margin-bottom: 5px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.setting-input,
.duration-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  background: #f9f9fb;
  color: #222;
  outline: none;
  transition: border 0.2s, background 0.2s;
  box-sizing: border-box;
  height: 32px;
  margin-top: 2px;
}

.setting-input:hover,
.duration-input:hover {
  background: #fff;
  border-color: #b1b5c3;
}

.setting-input:focus,
.duration-input:focus {
  border-color: #4f8cff;
}

/* Removed unused styles */

.color-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.color-picker-wrapper input[type="color"] {
  width: 36px;
  height: 36px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}

.file-upload-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.file-name {
  font-size: 12px;
  color: #666;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 4px 8px;
  background: #f5f5f5;
  border-radius: 4px;
}

.select-file-button {
  background: #4f8cff;
  color: #fff;
  border: none;
  padding: 7px 14px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
}

.select-file-button:hover {
  background: #6c63ff;
}

.switch-setting {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.switch-setting:hover {
  background-color: #f8f9fa;
}

.switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 32px;
  margin-left: 10px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e4e4e7;
  transition: .2s ease-in-out;
  border-radius: 34px;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.1);
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: .2s ease-in-out;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

input:checked + .slider {
  background-color: #4f8cff;
}

input:checked + .slider:before {
  transform: translateX(20px);
}

/* Add hover effects */
.slider:hover {
  background-color: #d4d4d8;
}

input:checked + .slider:hover {
  background-color: #3b82f6;
}

/* Active state */
.slider:active:before {
  width: 22px;
}

.font-selection {
  display: flex;
  align-items: center;
  gap: 10px;
}

.font-color-picker {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}
</style>
